<script setup>
/**
 * The wall's one picture: the app's own vehicle, rolling, with a rail under it
 * that says what Follow is for. A row of station dots, an alarm tick a stop
 * short, and the stop itself at the end.
 *
 * The vehicle is the loader's (`TrainScene`, drawn from `src/liveries.js`), in
 * the city's own train, or the tram or bus when that is what was being
 * followed. Reduced motion holds both the scene (TrainScene's own rule) and the
 * dot on the rail still.
 */
import { computed } from 'vue';
import TrainScene from './TrainScene.vue';
import { DEFAULT_LIVERY, livery as liveryOf } from '../liveries.js';
import { BUS_STREET, TRAM_STREET, backdropFor } from '../backdrops.js';

const props = defineProps({
  livery: { type: String, default: '' },
  rail: { type: Boolean, default: true },
});

const id = computed(() => props.livery || DEFAULT_LIVERY);
const world = computed(() => {
  const l = liveryOf(id.value);
  if (l.tram) return TRAM_STREET.id;
  if (l.road) return BUS_STREET.id;
  return backdropFor(id.value);
});

// Five stops; the alarm sits a little short of the last one.
// Melbourne's own scene is a level crossing, a fence and a station shell: right
// for a loader, clutter behind a train on a sheet. Held back here; every other
// city's backdrop (the Harbour Bridge) is kept.
const plain = computed(() => world.value === 'melbourne');

const STOPS = [6, 22, 38, 54, 94];
const ALARM = 70;
</script>

<template>
  <div class="hero" :class="{ plain }" data-testid="paywall-hero" aria-hidden="true">
    <div class="scene"><TrainScene :livery="id" :backdrop="world" /></div>
    <div v-if="rail" class="rail">
      <span class="rail-line" />
      <span v-for="(x, i) in STOPS" :key="i" class="rail-stop" :class="{ 'rail-end': i === STOPS.length - 1 }" :style="{ left: `${x}%` }" />
      <span class="rail-dot" />
      <span class="rail-alarm" :style="{ left: `${ALARM}%` }" />
      <span class="rail-tag rail-tag-alarm" :style="{ left: `${ALARM}%` }">ALARM</span>
      <span class="rail-tag rail-tag-end">YOUR STOP</span>
    </div>
  </div>
</template>

<style scoped>
.hero { display: block; }
.plain .scene :deep(.scenery) { display: none; }

.scene {
  width: min(100%, 300px);
  margin: 0 auto;
}
.scene :deep(svg) { display: block; width: 100%; height: auto; }

.rail {
  position: relative;
  height: 34px;
  margin: 4px 14px 0;
}

.rail-line {
  position: absolute;
  left: 0; right: 0; top: 7px;
  height: 2px;
  border-radius: 1px;
  background: var(--la-rule);
}

.rail-stop {
  position: absolute;
  top: 3px;
  width: 10px; height: 10px;
  margin-left: -5px;
  box-sizing: border-box;
  border-radius: 50%;
  background: var(--la-carriage);
  border: 2px solid var(--la-dim);
}
.rail-end {
  top: 1px;
  width: 14px; height: 14px;
  margin-left: -7px;
  background: var(--la-enamel);
  border-color: var(--la-enamel);
}

/* The alarm: a short tick standing off the rail, with its label under it. */
.rail-alarm {
  position: absolute;
  top: -1px;
  width: 2px; height: 18px;
  margin-left: -1px;
  background: var(--la-signal);
  border-radius: 1px;
}

.rail-tag {
  position: absolute;
  top: 19px;
  font-family: var(--la-font-mono);
  font-size: 11px;
  line-height: 14px;
  letter-spacing: 0.06em;
  color: var(--la-dim);
  white-space: nowrap;
}
.rail-tag-alarm { transform: translateX(calc(-100% + 1px)); color: var(--la-signal); }
.rail-tag-end { right: -6px; }

/* The vehicle's place on the rail, travelling from the first stop to the alarm. */
.rail-dot {
  position: absolute;
  top: 2px;
  width: 12px; height: 12px;
  margin-left: -6px;
  border-radius: 50%;
  background: var(--la-enamel);
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--la-enamel) 16%, transparent);
  left: 6%;
  animation: hero-roll 5.2s cubic-bezier(0.45, 0, 0.35, 1) infinite;
}
@keyframes hero-roll {
  0% { left: 6%; }
  80%, 100% { left: 70%; }
}
@media (prefers-reduced-motion: reduce) {
  .rail-dot { animation: none; left: 54%; }
}

@media (min-height: 800px) {
  .scene { width: min(100%, 310px); }
}

@media (max-height: 740px) {
  .scene { width: min(100%, 160px); }
  .rail { margin-top: 0; }
}
</style>
